import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, Users, Receipt, LayoutDashboard, ArrowRight, Compass, Lock } from "lucide-react";
import { getRoleFromToken } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { departamentosVisiveis, DEPARTAMENTOS_DESPESAS_GERAIS } from "../../constants/departamentosOrcamento";
import { ROLES_MAO_DE_OBRA } from "../../constants/maoDeObra";
import { ROLES_VISAO_EXECUTIVA_GERAL } from "../../constants/visaoExecutivaGeral";

// Paleta padrão da plataforma (mesma usada nas demais telas de Gestão Orçamentária)
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  bgActive: isLight ? "#F1E9FE" : "#241A3D",
});

// Passo a passo do fluxo de Despesas (demais departamentos, fora da Operação). É mais curto que
// o de Custos: não existe Alocação de Pessoal nem separação por projeto — a despesa é lançada
// direto no departamento. O controle de acesso também é misto: Despesas Mão de Obra e Visão
// Executiva Despesas expõem salário individual/agregado e ficam restritos (roles), enquanto
// Despesas Gerais é liberado por departamento (qualquer role que enxergue ao menos 1 dos
// departamentos listados em DEPARTAMENTOS_DESPESAS_GERAIS) — por isso cada passo guarda `roles`
// OU `departamentos`, nunca os dois, e a checagem de acesso trata os dois casos.
const PASSOS = [
  {
    numero: 1,
    icon: Users,
    titulo: "Despesas Mão de Obra",
    path: "/gestao-orcamentaria/despesas-mao-de-obra",
    roles: ROLES_MAO_DE_OBRA,
    resumo: "Cadastre os colaboradores dos demais departamentos (fora da Operação) e a composição de custo de cada um — salário, encargos e benefícios.",
    pontos: [
      "\"Nova linha\" cadastra um colaborador de qualquer departamento — é por aqui que entra uma contratação nova.",
      "Reajuste 1 e Reajuste 2 registram a evolução do colaborador ao longo do ano, cada um com seu mês de vigência — mesma lógica do Custo Mão de Obra da Operação.",
      "Diferente do Custo Mão de Obra (exclusivo da Operação), aqui todos os departamentos aparecem juntos na mesma tabela — use os filtros de coluna para focar no seu.",
    ],
  },
  {
    numero: 2,
    icon: Receipt,
    titulo: "Despesas Gerais",
    path: "/gestao-orcamentaria/despesas-gerais",
    departamentos: DEPARTAMENTOS_DESPESAS_GERAIS,
    resumo: "Lance as demais despesas do seu departamento (viagens, softwares, serviços...) pelos botões de categoria.",
    pontos: [
      "Se você tem acesso a mais de um departamento, o menu lateral mostra um seletor — escolha o departamento antes de lançar.",
      "\"Lançar Viagem\" usa um ticket médio único (\"Backoffice\"), compartilhado por todos os departamentos — diferente de Custos Gerais por Projeto, que trava um ticket por projeto.",
      "Não existe Alocação de Pessoal nem separação por projeto aqui: a despesa entra direto no departamento, sem passo intermediário.",
    ],
  },
  {
    numero: 3,
    icon: LayoutDashboard,
    titulo: "Visão Executiva Despesas",
    path: "/gestao-orcamentaria/visao-executiva-despesas",
    roles: ROLES_VISAO_EXECUTIVA_GERAL,
    resumo: "Acompanhe o resultado dos 2 passos anteriores: indicadores de mão de obra e concentração de gastos por categoria mãe de todos os departamentos.",
    pontos: [
      "Headcount, mix PJ/CLT, salário médio e reajustes agregados de todos os departamentos (fora da Operação).",
      "Concentração de gastos por categoria mãe, cruzando Despesas Mão de Obra × Despesas Gerais.",
      "Mesma lógica da Visão Executiva Custos, só que para os departamentos de Despesas.",
    ],
  },
];

// Passos com `roles` checam a lista diretamente (mesmo mecanismo de Boas-vindas Custos);
// passos com `departamentos` (ex: Despesas Gerais) seguem a mesma regra do menu lateral —
// liberado para quem enxerga pelo menos 1 dos departamentos do passo.
function temAcessoPasso(passo, role) {
  if (passo.departamentos) return departamentosVisiveis(role).some((d) => passo.departamentos.includes(d));
  return passo.roles.includes(role);
}

export default function BoasVindasDespesasView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();
  const role = getRoleFromToken();

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)", color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Boas-vindas · Despesas</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 12px",
              cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />} {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button onClick={() => navigate("/inicio")} style={{
              display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px",
              cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600,
            }}>
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24, width: "100%", maxWidth: 860, margin: "0 auto" }}>
          {/* Hero */}
          <section style={{
            display: "flex", alignItems: "flex-start", gap: 16,
            background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: "22px 24px",
          }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              width: 46, height: 46, borderRadius: 12, background: `${C.brand}1F`, color: C.brand,
            }}>
              <Compass size={24} />
            </div>
            <div>
              <h1 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 800, color: C.text }}>Bem-vindo à Gestão de Despesas (demais departamentos)</h1>
              <p style={{ margin: 0, fontSize: 13, color: C.muted, lineHeight: 1.6 }}>
                Este é o passo a passo recomendado para planejar e acompanhar as despesas dos departamentos
                fora da Operação: cadastre o pessoal, lance as demais despesas do departamento e acompanhe
                tudo consolidado na Visão Executiva. É mais curto que o fluxo de Custos — não existe Alocação
                de Pessoal nem lançamento por projeto aqui, e o acesso a cada passo varia: Despesas Mão de Obra
                e Visão Executiva ficam restritos por exporem salário, enquanto Despesas Gerais é liberado por
                departamento.
              </p>
            </div>
          </section>

          {/* Passo a passo */}
          <section style={{ display: "flex", flexDirection: "column" }}>
            {PASSOS.map((passo, idx) => {
              const Icon = passo.icon;
              const ultimo = idx === PASSOS.length - 1;
              const temAcesso = temAcessoPasso(passo, role);
              return (
                <div key={passo.numero} style={{ display: "flex", gap: 18 }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 36, flexShrink: 0 }}>
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      width: 36, height: 36, borderRadius: "50%", background: C.brand, color: "#fff",
                      fontSize: 15, fontWeight: 800,
                    }}>
                      {passo.numero}
                    </div>
                    {!ultimo && <div style={{ flex: 1, width: 2, background: C.border, marginTop: 6, marginBottom: 6, minHeight: 24 }} />}
                  </div>

                  <div style={{ flex: 1, paddingBottom: ultimo ? 0 : 22, minWidth: 0 }}>
                    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 20px", display: "flex", flexDirection: "column", gap: 10 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <Icon size={18} color={C.brand} />
                        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text }}>{passo.titulo}</h2>
                      </div>
                      <p style={{ margin: 0, fontSize: 13, color: C.text, lineHeight: 1.5 }}>{passo.resumo}</p>
                      <ul style={{ margin: 0, padding: "0 0 0 18px", display: "flex", flexDirection: "column", gap: 4 }}>
                        {passo.pontos.map((ponto, i) => (
                          <li key={i} style={{ fontSize: 12.5, color: C.muted, lineHeight: 1.5 }}>{ponto}</li>
                        ))}
                      </ul>
                      {temAcesso ? (
                        <button
                          onClick={() => navigate(passo.path)}
                          style={{
                            alignSelf: "flex-start", display: "flex", alignItems: "center", gap: 6,
                            background: "transparent", border: `1px solid ${C.brand}`, borderRadius: 8,
                            padding: "8px 14px", color: C.brand, fontSize: 12.5, fontWeight: 700,
                            cursor: "pointer", fontFamily: "inherit", marginTop: 4,
                          }}
                        >
                          Ir para {passo.titulo} <ArrowRight size={13} />
                        </button>
                      ) : (
                        <span style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, color: C.muted, marginTop: 4 }}>
                          <Lock size={12} /> Sem acesso a esta tela com o seu perfil atual
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </section>
        </main>
      </div>
    </div>
  );
}
